import type { LngLat } from "../adapters/types";

export const EARTH_RADIUS_METERS = 6_371_008.8;

const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Distance (mètres) entre deux points géographiques, sur une Terre sphérique (haversine). */
export function distanceMeters([lng1, lat1]: LngLat, [lng2, lat2]: LngLat): number {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Longueur (mètres) d'une polyligne. */
export function polylineLengthMeters(points: LngLat[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) total += distanceMeters(points[i - 1]!, points[i]!);

  return total;
}

/** Anneau sans son point de fermeture dupliqué, s'il en a un. */
function openRing(ring: LngLat[]): LngLat[] {
  const first = ring[0];
  const last = ring[ring.length - 1];

  return ring.length > 1 && first && last && first[0] === last[0] && first[1] === last[1] ? ring.slice(0, -1) : ring;
}

/** Périmètre (mètres) d'un anneau, fermé ou non. */
export function ringPerimeterMeters(ring: LngLat[]): number {
  const points = openRing(ring);
  if (points.length < 2) return 0;

  return polylineLengthMeters([...points, points[0]!]);
}

/**
 * Surface (m²) d'un anneau, fermé ou non — projeté localement sur un plan (équirectangulaire autour de
 * sa latitude moyenne), largement assez précis à l'échelle d'un bâtiment ou d'un quartier.
 */
export function ringAreaSquareMeters(ring: LngLat[]): number {
  const points = openRing(ring);
  if (points.length < 3) return 0;

  const meanLat = points.reduce((sum, [, lat]) => sum + lat, 0) / points.length;
  const metersPerDegLat = (Math.PI / 180) * EARTH_RADIUS_METERS;
  const metersPerDegLng = metersPerDegLat * Math.cos(toRad(meanLat));
  const projected = points.map(([lng, lat]) => [lng * metersPerDegLng, lat * metersPerDegLat] as const);

  let twiceArea = 0;
  for (let i = 0; i < projected.length; i++) {
    const [x1, y1] = projected[i]!;
    const [x2, y2] = projected[(i + 1) % projected.length]!;
    twiceArea += x1 * y2 - x2 * y1;
  }

  return Math.abs(twiceArea) / 2;
}

const decimal = (value: number, digits: number) =>
  value.toLocaleString("fr-FR", { minimumFractionDigits: digits, maximumFractionDigits: digits });

export type LengthUnit = "mm" | "cm" | "m";

/** « 12,4 m », « 245 m », « 1,25 km » ; « 432 cm », « 4 320 mm » dans une autre unité. */
export function formatDistance(meters: number, unit: LengthUnit = "m", options: { compact?: boolean } = {}): string {
  if (options.compact) unit = compactUnit(meters, unit);
  if (unit === "cm") return `${decimal(meters * 100, 0)} cm`;
  if (unit === "mm") return `${decimal(meters * 1000, 0)} mm`;
  if (meters >= 1000) return `${decimal(meters / 1000, 2)} km`;
  if (meters >= 100) return `${decimal(meters, 0)} m`;

  return `${decimal(meters, 1)} m`;
}

const UNIT_SCALE: [LengthUnit, number][] = [["mm", 1000], ["cm", 100], ["m", 1]];

function compactUnit(meters: number, unit: LengthUnit): LengthUnit {
  let index = UNIT_SCALE.findIndex(([candidate]) => candidate === unit);

  while (index < UNIT_SCALE.length - 1 && meters * UNIT_SCALE[index]![1] >= 1000) index++;

  return UNIT_SCALE[index]![0];
}

/** « 85 m² », « 1 250 m² », « 2,4 ha » ; au centième de m² sous 100 m² pour un plan en cm ou mm. */
export function formatArea(squareMeters: number, unit: LengthUnit = "m"): string {
  if (unit !== "m" && squareMeters < 100) return `${decimal(squareMeters, 2)} m²`;
  if (squareMeters >= 10_000) return `${decimal(squareMeters / 10_000, 1)} ha`;

  return `${decimal(squareMeters, 0)} m²`;
}

/**
 * Étiquette d'un tracé point par point, avec le point qui suit le curseur : ligne — segment en cours
 * et longueur totale ; polygone — segment en cours et surface s'il était fermé là. `null` sans point posé.
 */
export function draftMeasureText(points: LngLat[], preview: LngLat, polygon: boolean, unit: LengthUnit = "m"): string | null {
  const last = points[points.length - 1];
  if (!last) return null;

  const segment = formatDistance(distanceMeters(last, preview), unit);
  if (points.length < 2) return segment;
  if (polygon) return `${segment} · ${formatArea(ringAreaSquareMeters([...points, preview]), unit)}`;

  return `${segment} · total ${formatDistance(polylineLengthMeters([...points, preview]), unit, { compact: true })}`;
}

/** « 24 m × 12 m · 288 m² » pour les 4 coins d'un rectangle (dans l'ordre du contour). */
export function rectangleMeasureText(corners: LngLat[], unit: LengthUnit = "m"): string | null {
  if (corners.length < 4) return null;

  const width = distanceMeters(corners[0]!, corners[1]!);
  const height = distanceMeters(corners[1]!, corners[2]!);

  return `${formatDistance(width, unit)} × ${formatDistance(height, unit)} · ${formatArea(ringAreaSquareMeters(corners), unit)}`;
}

/** « rayon 12 m · 452 m² » pour un cercle donné par son centre et un point de son bord. */
export function circleMeasureText(center: LngLat, edge: LngLat, unit: LengthUnit = "m"): string {
  const radius = distanceMeters(center, edge);

  return `rayon ${formatDistance(radius, unit)} · ${formatArea(Math.PI * radius ** 2, unit)}`;
}
